import { AppLoading } from "@tracabois/components";
import { useRealizationMapQuery } from "../../api";
import { Stack, Typography, Box } from "@mui/material";
import { goto } from "../../../router/certificate.goto";
import { useNavigate } from "react-router-dom";
import { MapWithMarkers, MapMarker } from "@tracabois/components";
import { useBuildingTypeAllQuery, RealizationPopupCard } from "@tracabois/domain";
import { useMemo, useState } from "react";
import { RealizationMapQuery } from "../../model";
import { MapFilters } from "./MapFilters";
import { MapLegend } from "./MapLegend";

export interface RealizationsMapPageProps {}

export const RealizationsMapPage = (_: RealizationsMapPageProps) => {
  const navigate = useNavigate();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [departement, setDepartement] = useState("");
  const buildingTypeQuery = useBuildingTypeAllQuery();
  const buildingTypeItems = useMemo(() =>
    (buildingTypeQuery.data?.items ?? []).map((item) => ({ id: item.id, name: item.name })),
  [buildingTypeQuery.data]);
  const buildingTypeNameById = useMemo(() => {
    const map: Record<string, string> = {};
    buildingTypeItems.forEach((item) => { map[item.id] = item.name; });
    return map;
  }, [buildingTypeItems]);
  const buildingTypeNames = useMemo(() =>
    buildingTypeItems.map((item) => item.name),
  [buildingTypeItems]);

  // Use the new RealizationMapQuery
  const mapResult = useRealizationMapQuery({
    buildingTypes: selectedTypes.length > 0 ? selectedTypes as any[] : undefined,
    departement: departement || undefined,
  } as RealizationMapQuery);

  // Transform API markers to MapMarker format
  const markers: MapMarker[] = useMemo(() => {
    const items = mapResult.data?.markers ?? [];
    const result: MapMarker[] = [];

    for (const item of items) {
      const coords = item.coordinates;
      if (!coords) continue;

      result.push({
        id: item.id,
        position: [coords.latitude, coords.longitude],
        tooltip: item.name,
        popup: (
          <RealizationPopupCard
            name={item.name}
            thumbnail={item.thumbnail}
            onClick={() => goto.viewRealization(item.id, navigate)}
          />
        ),
        onClick: () => {
          // Optional: could auto-navigate on click instead of requiring popup button
        },
      });
    }

    return result;
  }, [mapResult.data?.markers, navigate]);

  if (mapResult.isLoading) {
    return <AppLoading />;
  }

  const total = mapResult.data?.total ?? 0;

  return (
    <Stack spacing={3} sx={{ p: 3, height: "100vh", boxSizing: "border-box" }}>
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Carte des bâtiments Bois des Alpes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {total} bâtiment{total !== 1 ? "s" : ""} référencé{total !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <MapFilters
        selectedTypes={selectedTypes}
        onTypesChange={setSelectedTypes}
        departement={departement}
        onDepartementChange={setDepartement}
        buildingTypeItems={buildingTypeItems}
        nameById={buildingTypeNameById}
      />

      <Box sx={{ flex: 1, position: "relative", minHeight: 400 }}>
        <MapWithMarkers
          markers={markers}
          height="100%"
          enableClustering={true}
          onMarkerClick={(id) => {
            // Could navigate directly: goto.viewRealization(id, navigate)
            console.log("Marker clicked:", id);
          }}
        />
        <MapLegend buildingTypes={buildingTypeNames} />
      </Box>
    </Stack>
  );
};
